/**
 * @fileoverview Incident Summary Note Hook - Manages incident summary as a staff note.
 *
 * The incident summary is stored as a staff note with the header prefix
 * "From Case Ingestion - Incident Summary". This hook handles finding,
 * parsing, and saving this note.
 */
import { useCallback, useMemo } from 'react';

import {
  useCreateStaffNoteMutation,
  useGetStaffNotesQuery,
  useUpdateStaffNoteMutation,
} from '@/services/api/staffNotesService';

import {
  extractNoteContent,
  formatIngestionNote,
  INCIDENT_SUMMARY_HEADER,
} from '../utils/notesParser';

/**
 * Manages incident summary stored as a staff note.
 *
 * The incident summary is stored as its own staff note with a header prefix
 * like "From Case Ingestion - Incident Summary".
 *
 * @param matterId - The matter ID to manage the note for
 */
export function useIncidentSummaryNote(matterId: string) {
  const { data: staffNotes, isLoading } = useGetStaffNotesQuery(
    { matterId },
    { skip: !matterId },
  );

  const [createStaffNote, { isLoading: isCreating }] =
    useCreateStaffNoteMutation();
  const [updateStaffNote, { isLoading: isUpdating }] =
    useUpdateStaffNoteMutation();

  // Find incident summary staff note
  const incidentSummaryNote = useMemo(
    () =>
      staffNotes?.find((n) => n.content.startsWith(INCIDENT_SUMMARY_HEADER)),
    [staffNotes],
  );

  // Parse existing data from the note
  const existingData = useMemo(
    () =>
      incidentSummaryNote
        ? extractNoteContent(incidentSummaryNote.content, INCIDENT_SUMMARY_HEADER)
        : '',
    [incidentSummaryNote],
  );

  // Save function: create or update the note
  const saveIncidentSummary = useCallback(
    async (content: string) => {
      const formattedContent = formatIngestionNote(
        INCIDENT_SUMMARY_HEADER,
        content,
      );

      if (incidentSummaryNote) {
        await updateStaffNote({
          matterId,
          noteId: incidentSummaryNote.id,
          note: { content: formattedContent },
        }).unwrap();
      } else {
        await createStaffNote({
          matterId,
          note: { content: formattedContent },
        }).unwrap();
      }
    },
    [matterId, incidentSummaryNote, createStaffNote, updateStaffNote],
  );

  return {
    /** Whether staff notes are still loading */
    isLoading,
    /** Whether a save operation is in progress */
    isSaving: isCreating || isUpdating,
    /** Parsed content from existing incident summary note */
    existingData,
    /** Save function - creates or updates the staff note */
    saveIncidentSummary,
  };
}
